/**
 * KAN-960: сохранение "Следующего контакта" и правило выбора менеджера.
 *
 * Кто берёт клиента в работу (то же правило, что "Взять в работу", workActions):
 *   - обычный менеджер: сам себе, manager_id не уходит (backend его не читает);
 *   - старший менеджер: он сам (менеджер сессии задан входом);
 *   - владелец и оператор с выбранным менеджером сессии: менеджер сессии;
 *   - владелец и оператор без менеджера сессии: выбор менеджера в модалке
 *     (по умолчанию текущий менеджер клиента).
 * После сохранения обновляются блок внимания (в нём CRM и "Встречи и перезвоны
 * на сегодня"), списки клиентов, карточка клиента и менеджеры; так же после
 * "Убрать контакт" (useClearNextContact, в работу не берёт).
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useSessionManager } from '@/hooks/useSessionManager';
import { NEXT_CONTACT_CLEARED_TEXT, NEXT_CONTACT_SAVED_TEXT } from '@/lib/nextContact';
import { workActions, type WorkSurface } from '@/lib/workActions';
import { clientQueryKey } from '@/services/api/clientQueries';
import { nextContactApi, type NextContactBody } from '@/services/api/nextContact';
import { ATTENTION_QUERY_KEY, MANAGERS_QUERY_KEY } from '@/services/api/queryKeys';

export interface NextContactManagerRule {
  /** В модалке выбор менеджера. */
  choose: boolean;
  /** Менеджер без выбора; undefined: manager_id не отправляется. */
  fixedManagerId: string | null | undefined;
}

export function useNextContactManager(surface: WorkSurface = 'cabinet'): NextContactManagerRule {
  const { data: user } = useCurrentUser();
  const { manager: sessionManager, locked } = useSessionManager();
  const work = workActions(user, surface);
  if (!work.assign) return { choose: false, fixedManagerId: undefined };
  if (!locked && sessionManager === null) return { choose: true, fixedManagerId: undefined };
  return { choose: false, fixedManagerId: sessionManager?.id ?? work.selfId };
}

function useInvalidateAfterContact(clientId: string) {
  const queryClient = useQueryClient();
  return () => {
    for (const queryKey of [ATTENTION_QUERY_KEY, ['clients'], clientQueryKey(clientId), MANAGERS_QUERY_KEY]) {
      void queryClient.invalidateQueries({ queryKey });
    }
  };
}

export function useSetNextContact(clientId: string) {
  const invalidate = useInvalidateAfterContact(clientId);
  return useMutation({
    mutationFn: (body: NextContactBody) => nextContactApi.set(clientId, body),
    onSuccess: () => {
      toast.success(NEXT_CONTACT_SAVED_TEXT);
      invalidate();
    },
  });
}

/** "Убрать контакт": шаг снимается с карточки, клиент в работу не берётся. */
export function useClearNextContact(clientId: string) {
  const invalidate = useInvalidateAfterContact(clientId);
  return useMutation({
    mutationFn: () => nextContactApi.clear(clientId),
    onSuccess: () => {
      toast.success(NEXT_CONTACT_CLEARED_TEXT);
      invalidate();
    },
  });
}
